import { FC } from 'react';
import { Button, Tag, Theme } from '@carbon/react';
import { ArrowLeft, Compare, Rocket, TaskComplete } from '@carbon/icons-react';
import { EventsPanelOpenButton } from 'components/events-panel';
import type { WellWorkspaceDetail } from 'types/well-workspace';
import styles from './workspace-header.module.scss';

interface Props {
  workspace: WellWorkspaceDetail;
  wellCount: number;
  promoteBusy: boolean;
  onBack: () => void;
  /** What Back says it does, since the host decides where it goes. */
  backLabel: string;
  onCompare: () => void;
  onPromote: () => void;
  /** Opens the Tasks panel. */
  onOpenTasks: () => void;
}

/**
 * Why Promote is unavailable, as the button's tooltip.
 *
 * The backend's own blockers where it sent them. A well authored in the workspace (Epic 3
 * Story 1) is gated on a well name AND a wellbore geometry, and naming which is missing is
 * the difference between a button a reader can act on and one that just looks broken. They
 * arrive as whole sentences, one per missing piece, so they are joined rather than reworded.
 *
 * The blockers are the ONLY channel those reasons have: the button is disabled while a
 * workspace is not promotable, so the 422 refusal that carries the same list can never be
 * provoked. Absent — a backend predating the field — the generic wording stands.
 * @param {WellWorkspaceDetail} workspace The workspace being shown.
 * @return {string | undefined} The tooltip, or undefined when Promote is available.
 */
const promoteHint = (workspace: WellWorkspaceDetail): string | undefined => {
  if (workspace.promoted) return 'Already promoted';
  if (workspace.promotable) return undefined;

  const blockers = workspace.promotionBlockers ?? [];
  return blockers.length > 0
    ? blockers.join('. ')
    : 'This workspace cannot be promoted';
};

// The dark workspace bar. Themed g90 so Carbon's own components (tags, focus rings)
// resolve their dark-surface tokens rather than being overridden one colour at a time.
export const WorkspaceHeader: FC<Props> = ({
  workspace,
  wellCount,
  promoteBusy,
  onBack,
  backLabel,
  onCompare,
  onPromote,
  onOpenTasks,
}) => (
  <Theme theme="g90">
    <div className={styles.bar}>
      <Button
        className={styles.ghost}
        kind="ghost"
        size="md"
        hasIconOnly
        renderIcon={ArrowLeft}
        // Named by the host, because where Back goes depends on where the workspace was
        // opened from: the well page returns to the well, Map Query to the listing, and
        // the deep-link route has no history of its own so it falls back to Map Query.
        iconDescription={backLabel}
        onClick={onBack}
      />
      <div className={styles.identity}>
        <h1 className={styles.title}>{workspace.name}</h1>
        <span className={styles.subtitle}>
          {wellCount === 1 ? '1 well' : `${wellCount} wells`}
        </span>
        <Tag type="cool-gray" size="sm">
          {workspace.kind}
        </Tag>
        {workspace.promoted ? (
          <Tag type="green" size="sm">
            Promoted
          </Tag>
        ) : (
          <Tag type="blue" size="sm">
            Draft
          </Tag>
        )}
      </div>
      <div className={styles.actions}>
        <Button
          className={styles.ghost}
          kind="ghost"
          size="md"
          renderIcon={Compare}
          onClick={onCompare}
        >
          Compare
        </Button>
        {/* The mock's "Tasks n/m". Shown only once the backend has verification items to
            count — Epic 1 seeds none, and a button reading "Tasks 0/0" over an empty
            panel just looked broken. It appears the moment there is something to show. */}
        {workspace.tasks.total > 0 && (
          <Button
            className={styles.ghost}
            kind="ghost"
            size="md"
            renderIcon={TaskComplete}
            onClick={onOpenTasks}
          >
            Tasks {workspace.tasks.completed}/{workspace.tasks.total}
          </Button>
        )}
        {/* Opens the shared activity panel, which the page mounts with the hrefs off
            this workspace's own `resources`. Only flips the panel's atom, so it needs
            no handler of its own — the same wiring the type-curve workspace bar uses. */}
        <EventsPanelOpenButton
          className={styles.ghost}
          tooltipPosition="bottom"
        />
        <Button
          size="md"
          renderIcon={Rocket}
          disabled={workspace.promoted || !workspace.promotable || promoteBusy}
          title={promoteHint(workspace)}
          onClick={onPromote}
        >
          {workspace.promoted
            ? 'Promoted'
            : promoteBusy
              ? 'Promoting…'
              : 'Promote'}
        </Button>
      </div>
    </div>
  </Theme>
);
